import { useState, useRef, useEffect } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { ChevronLeft } from 'lucide-react';
import ChartContainer from './ChartContainer';
import { useTheme } from '../../hooks/useTheme';
import {
  createChartAnimations,
  createHoverEffects,
  createEnhancedTooltips,
  createDrillDownAnimation,
  createInteractionEffects,
  ChartInteractionHandler
} from '../../utils/chartUtils';

ChartJS.register(ArcElement, Tooltip, Legend);

function CategoryBreakdownChart({ data, loading = false, onDrillDown }) {
  const chartRef = useRef();
  const [drillDownLevel, setDrillDownLevel] = useState(0);
  const [drillDownPath, setDrillDownPath] = useState([]);
  const [currentData, setCurrentData] = useState(null);
  const [interactionHandler] = useState(() => new ChartInteractionHandler(chartRef, {
    enableDrillDown: true,
    enableDetailedViews: true
  }));
  const [isDrillDownAnimating, setIsDrillDownAnimating] = useState(false);
  const [hoveredSegment, setHoveredSegment] = useState(null);
  const { theme } = useTheme();

  // Force chart update when theme changes
  useEffect(() => {
    if (chartRef.current) {
      const chart = chartRef.current;
      // Update chart colors based on current theme
      chart.options.plugins.legend.labels.color = getComputedStyle(document.documentElement).getPropertyValue('--text').trim();
      chart.update('none');
    }
  }, [theme]);

  // Use real data or empty structure if no data provided
  const defaultData = {
    level0: {
      title: 'Expense Categories',
      categories: []
    }
  };

  const chartData = data || defaultData;
  const displayData = currentData || chartData.level0;

  // Initialize interaction handlers
  useEffect(() => {
    if (interactionHandler) {
      // Register detailed view for category segments
      interactionHandler.registerDetailView('default', ({ elementIndex, chartData }) => {
        const category = displayData.categories[elementIndex];
        return {
          type: 'category-detail',
          data: {
            ...category,
            percentage: ((category.amount / displayData.categories.reduce((sum, cat) => sum + cat.amount, 0)) * 100).toFixed(1),
            subcategoryCount: category.subcategories?.length || 0
          }
        };
      });
    }
  }, [interactionHandler, displayData]);

  const handleDrillDown = (category, index) => {
    setIsDrillDownAnimating(true);

    const newDrillDownData = {
      title: category.name,
      categories: category.subcategories.map(sub => ({
        name: sub.name,
        amount: sub.amount,
        color: generateSubcategoryColor(category.color, category.subcategories.indexOf(sub))
      }))
    };

    // Animate the drill-down transition
    setTimeout(() => {
      setCurrentData(newDrillDownData);
      setDrillDownLevel(prev => prev + 1);
      setDrillDownPath(prev => [...prev, { name: category.name, index }]);
      setIsDrillDownAnimating(false);
    }, 350);

    if (onDrillDown) {
      onDrillDown(category, index);
    }
  };

  const handleBackToPrevious = () => {
    if (drillDownLevel > 0) {
      setDrillDownLevel(prev => prev - 1);
      setDrillDownPath(prev => prev.slice(0, -1));

      if (drillDownLevel === 1) {
        setCurrentData(null); // Back to root level
      }
    }
  };

  const handleReset = () => {
    setCurrentData(null);
    setDrillDownLevel(0);
    setDrillDownPath([]);
  };

  const generateSubcategoryColor = (baseColor, index) => {
    // Generate variations of the base color for subcategories
    const colors = [
      baseColor,
      adjustColorBrightness(baseColor, 20),
      adjustColorBrightness(baseColor, -20),
      adjustColorBrightness(baseColor, 40),
      adjustColorBrightness(baseColor, -40)
    ];
    return colors[index % colors.length];
  };

  const adjustColorBrightness = (hex, percent) => {
    // Simple color brightness adjustment
    const num = parseInt(hex.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    ...createDrillDownAnimation(isDrillDownAnimating ? 'in' : 'out'),
    ...createHoverEffects({
      enableGlow: true,
      enableScale: true,
      glowColor: 'rgba(168, 85, 247, 0.4)'
    }),
    ...createInteractionEffects({
      enableRipple: true,
      enableParticles: true
    }),
    ...createEnhancedTooltips({
      label: function (context) {
        const value = context.parsed;
        const total = context.dataset.data.reduce((a, b) => a + b, 0);
        const percentage = ((value / total) * 100).toFixed(1);
        return `${context.label}: ₹${value.toLocaleString('en-IN')} (${percentage}%)`;
      },
      footer: function (tooltipItems) {
        const context = tooltipItems[0];
        const elementIndex = context.dataIndex;
        const category = displayData.categories[elementIndex];

        if (category.subcategories && category.subcategories.length > 0) {
          return 'Click to drill down into subcategories';
        }
        return '';
      }
    }),
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: theme === 'dark' ? '#f8fafc' : '#1f2937',
          font: {
            size: 11
          },
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          generateLabels: function (chart) {
            const data = chart.data;
            const isDark = theme === 'dark';
            const labelColor = isDark ? '#f8fafc' : '#1f2937';
            if (data.labels.length && data.datasets.length) {
              return data.labels.map((label, i) => {
                const dataset = data.datasets[0];
                const value = dataset.data[i];
                const percentage = ((value / dataset.data.reduce((a, b) => a + b, 0)) * 100).toFixed(1);

                return {
                  text: `${label} (${percentage}%)`,
                  fillStyle: dataset.backgroundColor[i],
                  strokeStyle: dataset.borderColor[i],
                  lineWidth: dataset.borderWidth,
                  fontColor: labelColor,
                  hidden: false,
                  index: i
                };
              });
            }
            return [];
          }
        }
      }
    },
    onClick: (event, elements) => {
      if (elements.length > 0) {
        const elementIndex = elements[0].index;
        const category = displayData.categories[elementIndex];

        // Use advanced click handler
        const result = interactionHandler.handleAdvancedClick(event, elements, displayData, {
          onDrillDown: () => {
            if (category.subcategories && category.subcategories.length > 0) {
              handleDrillDown(category, elementIndex);
              return category;
            }
            return null;
          }
        });
      }
    },
    onHover: (event, elements) => {
      if (elements.length > 0) {
        setHoveredSegment(elements[0].index);
      } else {
        setHoveredSegment(null);
      }
    },
    cutout: '50%',
    radius: '80%',
    elements: {
      arc: {
        borderWidth: 2,
        hoverBorderWidth: 4,
        hoverOffset: 12
      }
    }
  };

  const chartDataConfig = {
    labels: displayData.categories.map(cat => cat.name),
    datasets: [
      {
        data: displayData.categories.map(cat => cat.amount),
        backgroundColor: displayData.categories.map((cat, index) => {
          const isHovered = hoveredSegment === index;
          const alpha = isHovered ? 0.9 : 0.7;
          return cat.color.replace(/[\d.]+\)$/g, `${alpha})`);
        }),
        borderColor: displayData.categories.map((cat, index) => {
          const isHovered = hoveredSegment === index;
          return isHovered ? '#ffffff' : cat.color;
        }),
        borderWidth: displayData.categories.map((_, index) => {
          return hoveredSegment === index ? 4 : 2;
        }),
        hoverBorderWidth: 6,
        hoverOffset: 15
      }
    ]
  };

  if (loading) {
    return (
      <ChartContainer
        title="Category Breakdown"
        subtitle="Interactive spending breakdown by category"
        loading={true}
        chartRef={chartRef}
        chartData={chartDataConfig}
      >
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--primary)' }}></div>
        </div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer
      title="Category Breakdown"
      subtitle="Click on segments to drill down • Interactive legend"
      chartRef={chartRef}
      chartData={chartDataConfig}
      onReset={handleReset}
      showDrillDownNavigation={drillDownLevel > 0}
      enableAnimations={true}
    >
      <div className="h-96 relative">
        {/* Drill-down navigation */}
        {drillDownLevel > 0 && (
          <div className="absolute top-0 left-0 z-10">
            <button
              onClick={handleBackToPrevious}
              className="flex items-center gap-1 px-2 py-1 rounded text-sm hover:opacity-80 transition-opacity"
              style={{
                background: 'var(--primary)',
                color: 'white'
              }}
            >
              <ChevronLeft className="w-4 h-4" />
              Back
            </button>
          </div>
        )}

        {/* Chart title */}
        <div className="absolute top-0 right-0 z-10">
          <h4 className="text-sm font-medium" style={{ color: 'var(--text)' }}>
            {displayData.title}
          </h4>
        </div>

        {/* Chart */}
        <div className="h-full pt-8">
          <Doughnut ref={chartRef} data={chartDataConfig} options={options} />
        </div>

        {/* Center text showing total */}
        <div className="absolute inset-0 pb-12 flex items-center justify-center pointer-events-none">
          <div className="text-center">
            <div className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
              ₹{displayData.categories.reduce((sum, cat) => sum + cat.amount, 0).toLocaleString('en-IN')}
            </div>
            <div className="text-xs" style={{ color: 'var(--muted)' }}>
              Total Spent
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumb Navigation */}
      {drillDownPath.length > 0 && (
        <div className="mt-4 flex items-center gap-2 text-sm" style={{ color: 'var(--muted)' }}>
          <button
            onClick={handleReset}
            className="hover:opacity-80 transition-opacity"
            style={{ color: 'var(--primary)' }}
          >
            All Categories
          </button>
          {drillDownPath.map((path, index) => (
            <span key={index} className="flex items-center gap-2">
              <span>›</span>
              <span>{path.name}</span>
            </span>
          ))}
        </div>
      )}

      {/* Category Summary */}
      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
        {displayData.categories.slice(0, 4).map((category, index) => {
          const total = displayData.categories.reduce((sum, cat) => sum + cat.amount, 0);
          const percentage = ((category.amount / total) * 100).toFixed(1);

          return (
            <div
              key={category.name}
              className="p-3 rounded-lg border text-center cursor-pointer hover:opacity-80 transition-opacity"
              style={{
                background: 'var(--panel)',
                borderColor: 'var(--border)'
              }}
              onClick={() => category.subcategories && handleDrillDown(category, index)}
            >
              <div
                className="w-4 h-4 rounded-full mx-auto mb-2"
                style={{ backgroundColor: category.color }}
              />
              <div className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                {category.name}
              </div>
              <div className="text-lg font-semibold" style={{ color: 'var(--text)' }}>
                ₹{(category.amount / 1000).toFixed(0)}K
              </div>
              <div className="text-xs" style={{ color: 'var(--muted)' }}>
                {percentage}%
              </div>
            </div>
          );
        })}
      </div>
    </ChartContainer>
  );
}

export default CategoryBreakdownChart;